import { useRef, useState, type ReactNode } from "react";
import type { EntitlementsDto } from "../api/client.js";
import { useSession } from "../api/session.js";
import { navigate } from "../router.js";

/** Session key AuthGate reads to pick the active workspace after a reload. */
export const ACTIVE_TENANT_KEY = "corefinops.activeTenant";

/**
 * Plan status strip (ENT-02 surfaced in the UI). The server enforces every
 * limit; this only explains the state and offers the next step: a demo
 * workspace → create your own; a trial → its end date; grace / read-only →
 * billing.
 */
export function PlanBanner({ entitlements: e }: { entitlements: EntitlementsDto }): ReactNode {
  if (e.isDemo) return <DemoBanner />;
  if (e.effectiveStatus === "active" && e.billingSource !== "trial") return null;
  const tone = e.effectiveStatus === "inactive" ? "danger" : e.effectiveStatus === "grace" ? "warn" : "info";
  const text =
    e.effectiveStatus === "trialing"
      ? `Free trial${e.trialEndsAt ? ` — ends ${e.trialEndsAt.slice(0, 10)}` : ""}. Up to ${e.limits.maxAwsAccounts} AWS account(s) and ${e.limits.maxMembers} members.`
      : e.reason ?? "Your subscription needs attention.";
  return (
    <div className={`cfo-banner cfo-banner-${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <span>{text}</span>
      <button type="button" className="cfo-btn-ghost" onClick={() => navigate("/settings/billing")}>
        {e.effectiveStatus === "trialing" ? "See plans" : "Plan & billing"}
      </button>
    </div>
  );
}

function DemoBanner(): ReactNode {
  const { client } = useSession();
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Same name → same Idempotency-Key, so a double click cannot create two workspaces.
  const keyRef = useRef<{ name: string; key: string } | null>(null);
  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (keyRef.current?.name !== name.trim()) keyRef.current = { name: name.trim(), key: `ws-${crypto.randomUUID()}` };
      const r = await client.post<{ tenantId: string }>("/me/workspaces", { name: name.trim(), currency: "USD" }, { "idempotency-key": keyRef.current.key });
      try {
        window.sessionStorage.setItem(ACTIVE_TENANT_KEY, r.tenantId);
      } catch {
        /* storage unavailable: the workspace picker appears instead */
      }
      window.location.assign("/overview");
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="cfo-banner cfo-banner-info" role="status">
      <span>You are exploring a <strong>demo workspace</strong> with sample data. Nothing here touches a real AWS account.</span>
      {open ? (
        <span className="cfo-inline-form">
          <label className="cfo-sr-only" htmlFor="demo-ws-name">Workspace name</label>
          <input id="demo-ws-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Company name" />
          <button type="button" className="cfo-btn-primary" disabled={busy || name.trim().length < 2} onClick={() => void create()}>
            {busy ? "Creating…" : "Create"}
          </button>
          {err ? <span role="alert" className="cfo-error">{err}</span> : null}
        </span>
      ) : (
        <button type="button" className="cfo-btn-primary" onClick={() => setOpen(true)}>Create my own workspace</button>
      )}
    </div>
  );
}
